import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
  ImageSplitFormSection,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import {activateCardConstants} from '../../../utils/constants/activate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { ActivateCardInputs } from './sections';
import { useCallback, useRef, useState } from 'react';
import { IconSectionCard } from '../../../shared/iconSectionCardSection';
import axios from 'axios';
import { globalConstants } from '../../../utils/constants/global';
import {OTPModal} from "../../modals/OTPModal";
import * as React from "react";
import ReCAPTCHA from 'react-google-recaptcha';
const { activationCode, firstName, lastName, phoneNumber} =
  activateCardConstants.validations;

declare global {
  interface Window {
    grecaptcha: any;
  }
}

type ModalState = {phone: string | null, open: false, token: string | null} | {phone: string, token: string | null, open: true, resolve: (result: boolean) => void}

export const ActivateCard = () => {
  const dispatch = useContextDispatch();
  const { loading } = useContextState();
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [isCaptchaCompleted, setCaptchaCompleted] = useState(false);
  const recaptcha = useRef<ReCAPTCHA>(null);

  const resetCaptcha = useCallback(() => {
    recaptcha.current?.reset();
    setCaptchaCompleted(false);
    setCaptchaToken("");
  }, []);

  const handleCaptchaChange = useCallback((value: string | null) => {
    setCaptchaCompleted(Boolean(value));
    setCaptchaToken(value || "");
  }, []);
  const requiresOtp = activateCardConstants.requiresOTP === 'true'

  const [serverErrors, setServerErrors] = useState('');

  const { heading, subHeading, activateCardText, activateCardButtonText } =
    activateCardConstants;
  const { expiredCardMessage } = globalConstants;

  const { push } = useHistory();
  const [modalState, setModalState] = useState<ModalState>({phone: null, open: false, token: null})

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      activationCode: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      termsConditionsCheck: false,
    },
  });
  const handleClose = () => {
   if (modalState.open) {
        modalState.resolve(true)
        setModalState({phone: '',open: false, token:null})
    }
  }
  const handleIsAuthed = (token:string | null) => {
    if (modalState.open) {
      modalState.resolve(true)
      setModalState({phone: '',open: false, token:token})
    }
  }
  const requestOtp = async (phone: string) => {
    try{
      const data = {
        mobile_number: phone
      };
      await axios.post('/v1/requestotp', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      });
      await new Promise((resolve) => {
        setModalState({open: true, token: null, phone: phone, resolve})
      } )
    }
    catch (err:any) {
      console.log('err', err);
      if (err.response.status === 400 || err.response.status === 401) {
        setServerErrors(err.response.data.message);
      }
    }
  }

  const authHeader = (token: string) => {
    // return auth header with jwt if a OTP was required for activation
    if (requiresOtp){
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  }

  const onFormSubmit = useCallback(async (formData: any) => {
    if (!isCaptchaCompleted) {
      setServerErrors('Captcha not verified');
      return;
    }
    if (!modalState.token){
      await requestOtp(formData.phoneNumber)
    } else{
      try {
        dispatch('START_LOADING');
        const data = {
          fname: formData.firstName,
          lname: formData.lastName,
          eaid: formData.activationCode,
          captchaToken: captchaToken,
        };
        debugger;
        const header = authHeader(modalState.token);
        await axios.post('/v1/activatecard', data, {
          headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || '', ...header
        }});
        dispatch('STOP_LOADING');
        push('/success');
      }
      catch (err:any) {
        console.log('err', err);
        if (err.response.status === 400 || err.response.status === 401) {
          setServerErrors(err.response.data.message);
        } else if (err.response.status === 700) {
          setServerErrors(expiredCardMessage);
        }
        resetCaptcha()
        dispatch('STOP_LOADING');
      }
    }
}, [authHeader, captchaToken, dispatch, expiredCardMessage, isCaptchaCompleted, modalState.token, push, resetCaptcha]);

  return (
    <>
      <Line />
      { modalState.open && <OTPModal {...modalState} handleClose={handleClose} handleIsAuthed={handleIsAuthed} /> }
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onFormSubmit)}>
          <SectionContainer>
            <Markdown children={heading} />
            <Markdown children={subHeading} />
            <Markdown children={activateCardText} />
            <FormContainer>
              <FormSectionContainer className="activateCardSection">
                <ActivateCardInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey="6LfU8UspAAAAAGYvuYdOkXrHcGGpuEXM4-hxH7Rh"
                  onChange={handleCaptchaChange}
                />
                <ButtonGroupContainer>
                  <Button
                    type="submit"
                    onClick={() => {
                      methods.handleSubmit(onFormSubmit);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : modalState.token != null  ? activateCardButtonText : 'Request OTP'}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
              <FormSectionContainer>
                {process.env.REACT_APP_CARD_OR_ICONS === 'CARD' ? (
                  <ImageSplitFormSection
                    src={process.env.REACT_APP_CARD_FRONT ?? ''}
                  />
                ) : (
                  <IconSectionCard />
                )}
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    </>
  );
};
const validationSchema = Yup.object().shape({
  activationCode: Yup.string()
    .required(activationCode.required)
    .matches(/^[a-zA-Z0-9_.-]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  firstName: Yup.string()
    .required(firstName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: firstName.format,
      excludeEmptyString: true,
    }),
  lastName: Yup.string()
    .required(lastName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: lastName.format,
      excludeEmptyString: true,
    }),
  phoneNumber: Yup.string()
      .required(phoneNumber.required)
      .matches(/^\+61[0-9]+$/, {
        message: phoneNumber.format,
        excludeEmptyString: true,
      })
      .min(12)
      .max(12),
});
